import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { plansApi } from "@/api/plans";

export const Route = createFileRoute("/app/projects/$pid/payments")({
  component: PaymentsPage,
});

function PaymentsPage() {
  const { pid } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["payments", pid], queryFn: () => plansApi.payments(pid) });
  const cancel = useMutation({
    mutationFn: (paymentId: string) => plansApi.cancelPayment(pid, paymentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payments", pid] });
      qc.invalidateQueries({ queryKey: ["project-plan", pid] });
    },
  });

  return (
    <div className="card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
          <tr>
            <th className="px-4 py-3">Received</th>
            <th className="px-4 py-3">Amount (USDC)</th>
            <th className="px-4 py-3">Promo</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Tx</th>
            <th className="px-4 py-3">Extends to</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? <tr><td colSpan={7} className="px-4 py-6 text-ink/55">Loading…</td></tr>
          : data && data.length > 0 ? data.map((p) => {
            // Pending payments are unfinished — clicking the row drops the
            // user back on the deposit-address page to complete it.
            const resumable = p.status === "pending";
            return (
            <tr
              key={p.id}
              onClick={resumable ? () => navigate({ to: "/app/projects/$pid/pay/$paymentId", params: { pid, paymentId: p.id } }) : undefined}
              className={`border-t border-ink/8 ${resumable ? "cursor-pointer hover:bg-cream" : ""}`}
            >
              <td className="px-4 py-3">{new Date(p.received_at).toLocaleString()}</td>
              <td className="px-4 py-3 font-medium">
                ${p.amount_usdc}
                {p.discount_usd && Number(p.discount_usd) > 0 && p.list_price_usd && (
                  <span className="ml-2 text-xs text-ink/45 line-through">${p.list_price_usd}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {p.applied_promo_code ? (
                  <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-700">
                    {p.applied_promo_code}
                    {p.discount_usd && Number(p.discount_usd) > 0 ? ` (−$${Number(p.discount_usd)})` : ""}
                  </span>
                ) : (
                  <span className="text-ink/45">—</span>
                )}
              </td>
              <td className="px-4 py-3">
                <StatusBadge status={p.status} />
                {resumable && <span className="ml-2 text-xs text-primary-600">Complete →</span>}
              </td>
              <td className="px-4 py-3">
                {p.tx_signature ? (
                  <a href={`https://solscan.io/tx/${p.tx_signature}`} target="_blank" rel="noreferrer" className="font-mono text-xs text-primary-600">
                    {p.tx_signature.slice(0, 10)}…
                  </a>
                ) : <span className="text-ink/45">—</span>}
              </td>
              <td className="px-4 py-3 text-ink/55">{p.extends_to ? new Date(p.extends_to).toLocaleDateString() : "—"}</td>
              <td className="px-4 py-3 text-right">
                {resumable && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm("Cancel this pending payment?")) cancel.mutate(p.id);
                    }}
                    disabled={cancel.isPending}
                    className="text-xs text-red-600 hover:underline disabled:opacity-50"
                  >
                    Cancel
                  </button>
                )}
              </td>
            </tr>
            );
          }) : <tr><td colSpan={7} className="px-4 py-6 text-ink/55">No payments yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const c = {
    confirmed: "bg-green-500/15 text-green-600",
    pending:   "bg-amber-500/15 text-amber-600",
    refunded:  "bg-ink/8 text-ink/75",
    rejected:  "bg-red-500/15 text-red-600",
  }[status] ?? "bg-ink/4 text-ink/75";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${c}`}>{status}</span>;
}
